import { useEffect, useState } from 'react';
import { useSaveData } from '../hooks/useSaveData';

import { fileUploadApiKeyStore } from '../api/file-upload-key-store';

// Type definition for additional data
interface AdditionalData {
  fileID?: number;
  contractID?: number;
  planID?: number;
  planFileID?: number;
  fileSection?: string;
  fileName?: string;
  fileType?: string;
  workOrderID?: number;
  unitID?: number;
  purchaseOrderID?: number;
  proctorResultsID?: number;
  coCURL?: string;
}

// Type definition for download file function parameters
interface DownloadFileParams {
  additionalData?: AdditionalData; // Optional additional data to include with a request
}

// Type definition for the hook's return structure
interface UseFileDownload {
  isLoading: boolean; // Loading state
  error: Error | null; // Error state
  downloadFile: (params: DownloadFileParams) => Promise<void>; // Download function
}

// Helper function to create and download a Blob
const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  document.body.appendChild(link);
  link.click(); // Initiate download
  document.body.removeChild(link); // Clean up link
  window.URL.revokeObjectURL(url); // Clean up the created object URL
};

// Custom hook for downloading files with optional data mutation
export const useFileDownload = (): UseFileDownload => {
  const [error, setError] = useState<Error | null>(null);
  const [additionalData, setAdditionalData] = useState<AdditionalData>({});

  const { mutateAsync, data, isLoading } = useSaveData(fileUploadApiKeyStore.downloadFile());

  useEffect(() => {
    if (data) {
      if (typeof data === 'string') {
        const byteCharacters = atob(data); // Convert Base64 to byte characters

        // Step 2: Convert byte characters to an array of byte numbers
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0)); // Array of byte values

        // Step 3: Create a Uint8Array from the byte numbers
        const byteArray = new Uint8Array(byteNumbers); // Create a typed array from byte numbers

        // Step 4: Create a Blob using the byte array with the correct MIME type
        const blob = new Blob([byteArray], { type: additionalData?.fileType });

        // const blob = new Blob([data], { type: additionalData?.fileType });

        downloadBlob(blob, additionalData?.fileName ?? 'file'); // Helper function for downloading
      } else {
        throw new Error('Invalid data type for file download.');
      }
    }
  }, [data]);

  const downloadFile = async ({ additionalData: newAdditionalData = {} }: DownloadFileParams): Promise<void> => {
    setError(null);

    const {
      fileID = 0,
      workOrderID = 0,
      contractID = 0,
      planID = 0,
      planFileID = 0,
      purchaseOrderID = 0,
      unitID = 0,
      proctorResultsID = 0,
      fileSection = 'string',
      fileName = 'downloaded_file',
      coCURL = 'string',
    } = newAdditionalData;

    setAdditionalData((prevData) => ({
      ...prevData, // Maintain previous state
      ...newAdditionalData, // Add or update with new data
    }));

    try {
      // If there's additional data, send it to the server
      await mutateAsync({
        data: {
          fileID,
          contractID,
          planID,
          planFileID,
          fileSection,
          workOrderID,
          unitID,
          purchaseOrderID,
          proctorResultsID,
          coCURL,
        },
      });
    } catch (err) {
      setError(err as Error); // Handle errors
    }
  };

  return {
    error,
    isLoading,
    downloadFile,
  };
};
